import { toast } from "sonner";
import { SCORING_PHRASES } from "./constants";

// Helper to ensure inning_scores array is properly initialized
export const ensureInningScores = (scores, totalInnings = 9) => {
  const result = scores ? scores.map(s => ({ ...s })) : [];
  while (result.length < totalInnings) {
    result.push({ top: null, bottom: null });
  }
  return result;
};

// Helper to show scoring toast with player names
export const showScoringToast = (scoringNames, context = "") => {
  if (!scoringNames || scoringNames.length === 0) return;
  
  const count = Math.min(scoringNames.length, 4);
  const phrases = SCORING_PHRASES[count];
  let message = phrases[Math.floor(Math.random() * phrases.length)];
  
  scoringNames.forEach((name, idx) => {
    message = message.replace(`{${idx + 1}}`, name);
  });
  
  if (context) message = `${context} ${message}`;
  toast.success(message);
};

// Helper to check if game is over
export const isGameOver = (gameState, totalInnings = 9) => {
  const { inning, half, home_score, away_score, outs } = gameState;
  const finalInning = totalInnings;

  if (inning < finalInning) return false;

  // `outs === 0` alone is ambiguous because a leadoff scoring play (HR / scoring
  // walk) leaves outs at 0 mid-half. flip_half_inning resets batters_faced_inning
  // to 0, so the combination pins us to the post-flip moment.
  const atHalfStart = (gameState.batters_faced_inning ?? 0) === 0;

  // After top of final inning, home team is winning
  if (inning === finalInning && half === "bottom" && home_score > away_score && outs === 0 && atHalfStart) {
    return { winner: "home", reason: "regulation" };
  }

  // Walk-off - Bottom of 9th (or extra) and home team takes the lead
  if (inning >= finalInning && half === "bottom" && home_score > away_score) {
    return { winner: "home", reason: "walk-off" };
  }

  // Away team wins — only valid at the post-flip moment (atHalfStart). Without
  // this guard a leadoff scoring play in top-of-extras would end the game before
  // the home team gets to bat in the bottom.
  //
  // The reason depends on HOW FAR past the final inning we are. A completed
  // bottom of the 9th flips the state to inning 10 / top, so a plain regulation
  // away win lands here looking identical to an extra-innings one — which is why
  // a 14-7 nine-inning game reported "extra-innings", and why the value appeared
  // to mutate: the derived fallback said "regulation" first and this replaced it.
  // Exactly one inning past the final is regulation; beyond that is extras.
  if (inning > finalInning && half === "top" && outs === 0 && away_score > home_score && atHalfStart) {
    return {
      winner: "away",
      reason: inning === finalInning + 1 ? "regulation" : "extra-innings",
    };
  }

  return false;
};

// Get the active batting lineup. Prefers explicit batting_order; falls back to non-pitcher
// non-subbed players in roster order so games with a missing/empty batting_order (e.g. older
// online games created before the preset-fix) still render a valid lineup and batter name.
export const getLineup = (team) => {
  if (!team || !team.players) return [];
  const order = team.batting_order || [];
  const playerMap = {};
  team.players.forEach(p => { playerMap[p.id] = p; });
  if (order.length > 0) {
    return order.map(id => playerMap[id]).filter(p => p && !p.subbed_out);
  }
  return team.players.filter(p => p.position !== "P" && !p.subbed_out).slice(0, 9);
};

// Get bench players (not in lineup, not subbed out, non-pitchers)
export const getBench = (team) => {
  const lineupIds = new Set(team.batting_order || []);
  return team.players.filter(p => p.position !== "P" && !lineupIds.has(p.id) && !p.subbed_out);
};

// Get the current batter index for the batting team
export const getBatterIndex = (state) => {
  if (!state) return 0;
  if (state.half === "top") {
    return state.away_batter_index ?? state.current_batter_index ?? 0;
  } else {
    return state.home_batter_index ?? state.current_batter_index ?? 0;
  }
};

// Get the batter index key for updates
export const getBatterIndexKey = (half) => {
  return half === "top" ? "away_batter_index" : "home_batter_index";
};

// Safely advance the batter index. Guards against `(x + 1) % 0 = NaN` when the lineup is
// momentarily empty (e.g. during state transitions). NaN serializes as JSON null and breaks
// server validation. Always returns a non-negative integer.
export const nextBatterIndex = (state, battersLength) => {
  const current = getBatterIndex(state) || 0;
  if (!battersLength || battersLength <= 0) return current;
  return (current + 1) % battersLength;
};

// Get team based on half
export const getBattingTeam = (game) => {
  return game.state.half === "top" ? game.away_team : game.home_team;
};

export const getFieldingTeam = (game) => {
  return game.state.half === "top" ? game.home_team : game.away_team;
};

// Get current batter
export const getCurrentBatter = (game) => {
  const battingTeam = getBattingTeam(game);
  const lineup = getLineup(battingTeam);
  const batterIndex = getBatterIndex(game.state) % (lineup.length || 1);
  return lineup[batterIndex];
};

// Get current pitcher
export const getCurrentPitcher = (game) => {
  const fieldingTeam = getFieldingTeam(game);
  return fieldingTeam.players.find(p => p.position === "P" && !p.subbed_out);
};

// Deterministic HR distance: same (power, seed) → same number every call.
// HRBallEffect AND PitchingPhase/FieldingPhase both call this with the resolution_id
// as the seed, so the cinematic counter and the phase-tab badge agree exactly without
// any prop drilling. Mirrors the bias math in HRBallEffect.jsx (kept as the source of
// truth for the distance band; this is a thin wrapper).
const _HR_DIST_MIN = 412;
const _HR_DIST_MAX = 517;
const _HR_POWER_MIN = 7;
const _HR_POWER_MAX = 14;
const _HR_FLOOR_LIFT_AT_MAX = 50;
const _HR_BIAS_AT_MIN_POWER = 2.5;
const _HR_BIAS_AT_MAX_POWER = 0.7;

// FNV-1a 32-bit hash of the seed string → [0, 1). Avoids pulling in a hashing dep
// and is stable across runs (Math.imul-based, no platform variance).
function _hash01(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 0x100000000;
}

export function rollHrDistance(power, seed) {
  const p = Math.max(_HR_POWER_MIN, Math.min(_HR_POWER_MAX, power || _HR_POWER_MIN));
  const t = (p - _HR_POWER_MIN) / (_HR_POWER_MAX - _HR_POWER_MIN);
  const minD = _HR_DIST_MIN + Math.floor(t * _HR_FLOOR_LIFT_AT_MAX);
  const exponent = _HR_BIAS_AT_MIN_POWER - t * (_HR_BIAS_AT_MIN_POWER - _HR_BIAS_AT_MAX_POWER);
  const r0 = seed != null ? _hash01(String(seed)) : Math.random();
  const r = Math.pow(r0, exponent);
  return minD + Math.floor(r * (_HR_DIST_MAX - minD + 1));
}

// ERA for display. Coerces BOTH operands on purpose.
//
// The recurring "ERA: NaN" was NOT a divide-by-zero — it was an undefined NUMERATOR.
// backend/game_stats.py builds its Mongo $inc delta with a helper that skips any
// counter whose value is 0, and $inc on a dotted path only creates the fields it
// touches, so a server-side pitcher doc is SPARSE: a batted-ball out stores
// {outs: 1} with no runsAllowed key at all. Guarding on `outs > 0` therefore does
// not help — the guard passes and undefined/1*27 yields NaN. Any fix that only
// hardens the denominator will not stop it.
//
// Returns "—" for a pitcher who has not recorded an out, and "INF" if runs scored
// before the first out (a real, if unkind, statistic).
export const formatEra = (runsAllowed, outs) => {
  const ra = Number(runsAllowed) || 0;
  const o = Number(outs) || 0;
  if (o <= 0) return ra > 0 ? "INF" : "—";
  return ((ra / o) * 27).toFixed(2);
};
